package com.fantasytenis.shared.data.auth.repository

import com.fantasytenis.shared.core.network.ApiResponse
import com.fantasytenis.shared.data.auth.api.AuthApi
import com.fantasytenis.shared.data.auth.dto.*
import com.fantasytenis.shared.domain.auth.entity.AvatarType
import com.fantasytenis.shared.domain.auth.entity.KycStatus
import com.fantasytenis.shared.domain.auth.entity.UserEntity
import com.fantasytenis.shared.domain.auth.entity.UserRole

/**
 * Auth Repository Interface.
 *
 * Defines the contract for authentication data operations.
 */
interface AuthRepository {
    suspend fun register(email: String, password: String, firstName: String, lastName: String, username: String): Result<AuthData>
    suspend fun login(email: String, password: String): Result<AuthData>
    suspend fun logout(): Result<Unit>
    suspend fun refreshToken(refreshToken: String): Result<TokenData>
    suspend fun getCurrentUser(): Result<UserEntity>
    suspend fun updateUser(
        firstName: String? = null,
        lastName: String? = null,
        phoneNumber: String? = null,
        avatarUrl: String? = null,
        username: String? = null,
        avatarType: String? = null,
        avatarPresetId: String? = null
    ): Result<UserEntity>
    suspend fun changePassword(currentPassword: String, newPassword: String): Result<Unit>
    suspend fun checkUsernameAvailable(username: String): Result<CheckUsernameData>
    suspend fun getAvatarUploadUrl(contentType: String): Result<AvatarUploadData>
    suspend fun getPresetAvatars(): Result<PresetAvatarsData>
}

/**
 * Auth data returned from login/register.
 */
data class AuthData(
    val accessToken: String,
    val refreshToken: String,
    val user: UserEntity
)

/**
 * Token data returned from refresh.
 */
data class TokenData(
    val accessToken: String,
    val refreshToken: String
)

/**
 * Auth Repository Implementation.
 */
class AuthRepositoryImpl(
    private val authApi: AuthApi
) : AuthRepository {

    override suspend fun register(email: String, password: String, firstName: String, lastName: String, username: String): Result<AuthData> {
        val request = RegisterRequest(
            email = email,
            password = password,
            firstName = firstName,
            lastName = lastName,
            username = username
        )

        return when (val response = authApi.register(request)) {
            is ApiResponse.Success -> {
                Result.success(
                    AuthData(
                        accessToken = response.data.accessToken,
                        refreshToken = response.data.refreshToken,
                        user = response.data.user.toEntity()
                    )
                )
            }
            is ApiResponse.Error -> Result.failure(Exception(response.message))
        }
    }

    override suspend fun login(email: String, password: String): Result<AuthData> {
        val request = LoginRequest(email = email, password = password)

        return when (val response = authApi.login(request)) {
            is ApiResponse.Success -> {
                Result.success(
                    AuthData(
                        accessToken = response.data.accessToken,
                        refreshToken = response.data.refreshToken,
                        user = response.data.user.toEntity()
                    )
                )
            }
            is ApiResponse.Error -> Result.failure(Exception(response.message))
        }
    }

    override suspend fun logout(): Result<Unit> {
        return when (val response = authApi.logout()) {
            is ApiResponse.Success -> Result.success(Unit)
            is ApiResponse.Error -> Result.failure(Exception(response.message))
        }
    }

    override suspend fun refreshToken(refreshToken: String): Result<TokenData> {
        val request = RefreshTokenRequest(refreshToken = refreshToken)

        return when (val response = authApi.refreshToken(request)) {
            is ApiResponse.Success -> {
                Result.success(
                    TokenData(
                        accessToken = response.data.accessToken,
                        refreshToken = response.data.refreshToken
                    )
                )
            }
            is ApiResponse.Error -> Result.failure(Exception(response.message))
        }
    }

    override suspend fun getCurrentUser(): Result<UserEntity> {
        return when (val response = authApi.getCurrentUser()) {
            is ApiResponse.Success -> Result.success(response.data.toEntity())
            is ApiResponse.Error -> Result.failure(Exception(response.message))
        }
    }

    override suspend fun updateUser(
        firstName: String?,
        lastName: String?,
        phoneNumber: String?,
        avatarUrl: String?,
        username: String?,
        avatarType: String?,
        avatarPresetId: String?
    ): Result<UserEntity> {
        val request = UpdateUserRequest(
            firstName = firstName,
            lastName = lastName,
            phoneNumber = phoneNumber,
            avatarUrl = avatarUrl,
            username = username,
            avatarType = avatarType,
            avatarPresetId = avatarPresetId
        )

        return when (val response = authApi.updateUser(request)) {
            is ApiResponse.Success -> Result.success(response.data.toEntity())
            is ApiResponse.Error -> Result.failure(Exception(response.message))
        }
    }

    override suspend fun checkUsernameAvailable(username: String): Result<CheckUsernameData> {
        return when (val response = authApi.checkUsernameAvailable(username)) {
            is ApiResponse.Success -> Result.success(response.data)
            is ApiResponse.Error -> Result.failure(Exception(response.message))
        }
    }

    override suspend fun getAvatarUploadUrl(contentType: String): Result<AvatarUploadData> {
        return when (val response = authApi.getAvatarUploadUrl(contentType)) {
            is ApiResponse.Success -> Result.success(response.data)
            is ApiResponse.Error -> Result.failure(Exception(response.message))
        }
    }

    override suspend fun getPresetAvatars(): Result<PresetAvatarsData> {
        return when (val response = authApi.getPresetAvatars()) {
            is ApiResponse.Success -> Result.success(response.data)
            is ApiResponse.Error -> Result.failure(Exception(response.message))
        }
    }

    override suspend fun changePassword(currentPassword: String, newPassword: String): Result<Unit> {
        val request = ChangePasswordRequest(
            currentPassword = currentPassword,
            newPassword = newPassword
        )

        return when (val response = authApi.changePassword(request)) {
            is ApiResponse.Success -> Result.success(Unit)
            is ApiResponse.Error -> Result.failure(Exception(response.message))
        }
    }
}

/**
 * Extension function to convert UserDto to UserEntity.
 */
private fun UserDto.toEntity(): UserEntity {
    return UserEntity(
        id = id,
        email = email,
        firstName = firstName,
        lastName = lastName,
        phoneNumber = phoneNumber,
        avatarUrl = avatarUrl,
        kycStatus = parseKycStatus(kycStatus),
        role = UserRole.fromString(role),
        createdAt = parseTimestamp(createdAt),
        username = username,
        avatarType = AvatarType.fromString(avatarType),
        avatarPresetId = avatarPresetId
    )
}

/**
 * Parse KycStatus from string.
 */
private fun parseKycStatus(status: String): KycStatus {
    return when (status.lowercase()) {
        "verified" -> KycStatus.VERIFIED
        "failed" -> KycStatus.FAILED
        else -> KycStatus.PENDING
    }
}

/**
 * Parse ISO timestamp string to Long (milliseconds since epoch).
 * Returns current time if parsing fails or null.
 */
private fun parseTimestamp(timestamp: String?): Long {
    if (timestamp == null) return kotlinx.datetime.Clock.System.now().toEpochMilliseconds()
    return try {
        kotlinx.datetime.Instant.parse(timestamp).toEpochMilliseconds()
    } catch (e: Exception) {
        kotlinx.datetime.Clock.System.now().toEpochMilliseconds()
    }
}
